// src/services/authService.js - Config ile Güncellenmiş Versiyon
import apiConfig from '../config/config'; // Merkezi config
import httpClient from './utils/httpClient';

class AuthService {
  constructor() {
    // Config'den API ayarlarını al
    this.API_BASE_URL = apiConfig.BASE_URL;
    this.TOKEN_KEY = 'tegim-token';
    this.USER_KEY = 'tegim-user';
    
    // Hata ayıklama bilgisi
    console.log('AuthService initialized with base URL:', this.API_BASE_URL);
  }

  // Token var mı kontrol et
  isAuthenticated() {
    const token = this.getToken();
    const user = this.getUser();
    return !!(token && user);
  }

  // Token'ı al
  getToken() {
    return localStorage.getItem(this.TOKEN_KEY);
  }

  // User bilgilerini al
  getUser() {
    try {
      const userJson = localStorage.getItem(this.USER_KEY);
      return userJson ? JSON.parse(userJson) : null;
    } catch (error) {
              console.error('❌ Kullanıcı verisi ayrıştırma hatası:', error);
      return null;
    }
  }

  // Login işlemi
  async login(username, password) {
    try {
      const directTokenUrl = (process.env.REACT_APP_TOKEN_URL || 'http://78.186.223.18:8762/api/token').trim();
      const loginUrl = directTokenUrl || apiConfig.auth.login;
      console.log('Login attempt to:', loginUrl);
      
      // HttpClient ile istek
      const response = await httpClient.post(loginUrl, { username, password }, { 'Content-Type': 'application/json' });

      if (response.ok) {
        const data = response.data?.data || response.data;
        
        // Token'ı kaydet
        const token = data.token || data.access_token || data;
        localStorage.setItem(this.TOKEN_KEY, token);
        
        // User bilgilerini kaydet
        const userData = {
          id: data.user?.id || 1,
          username: username,
          name: data.user?.name || username,
          email: data.user?.email || `${username}@api.com`,
          role: data.user?.role || 'user',
          loginTime: new Date().toISOString()
        };
        localStorage.setItem(this.USER_KEY, JSON.stringify(userData));
        
        console.log('Giriş başarılı:', userData.username);
        
        return { success: true, data: userData };
      } else {
        const errorData = response.data || {};
        throw new Error(errorData?.error?.message || errorData.message || 'Giriş başarısız');
      }
    } catch (error) {
      console.error('Giriş hatası:', error);
      throw error;
    }
  }

  // Logout işlemi
  logout() {
    try {
      console.log('Kullanıcı çıkış yapıyor...');
      
      // Local storage'ı temizle
      localStorage.removeItem(this.TOKEN_KEY);
      localStorage.removeItem(this.USER_KEY);
      
      // Opsiyonel: Sunucuya logout bildirimi gönder
      this.notifyServerLogout().catch(err => {
        console.warn('⚠️ Sunucu çıkış bildirimi başarısız:', err);
      });
      
      console.log('Çıkış tamamlandı');
    } catch (error) {
      console.error('Çıkış hatası:', error);
    }
  }

  // 📤 Sunucuya logout bildirimi
  async notifyServerLogout() {
    const token = this.getToken();
    if (!token || !apiConfig.ENDPOINTS.AUTH.LOGOUT) return;

    try {
      await httpClient.post(apiConfig.auth.logout, null, {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      });
      console.log('Server notified about logout');
    } catch (error) {
      // Logout notification hatası kritik değil
      console.warn('Server logout notification failed:', error);
    }
  }

  // 🌐 Token ile API isteği yap
  async apiRequest(endpoint, options = {}) {
    const token = this.getToken();
    
    if (!token) {
      throw new Error('Kimlik doğrulama tokenı bulunamadı');
    }

    // Token expired mi kontrol et
    if (this.isTokenExpired()) {
      console.warn('Token süresi doldu, çıkış yapılıyor...');
      this.logout();
      throw new Error('Token süresi doldu');
    }

    const defaultHeaders = {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    };

    const requestOptions = {
      ...options,
      headers: {
        ...defaultHeaders,
        ...options.headers
      }
    };

    try {
      // Full URL oluştur
      const fullURL = endpoint.startsWith('http') 
        ? endpoint 
        : `${this.API_BASE_URL}${endpoint}`;

      console.log(`API Request: ${requestOptions.method || 'GET'} ${fullURL}`);
      
      // HttpClient kullan
      const response = await httpClient.customRequest(fullURL, requestOptions.method || 'GET', requestOptions.headers, requestOptions.body || null, { timeout: apiConfig.TIMEOUT });
      
      // Token geçersizse logout yap
      if (response.status === 401) {
        console.warn('Authentication expired (401), logging out...');
        this.logout();
        throw new Error('Kimlik doğrulama süresi doldu');
      }
      
      if (!response.ok) {
        throw new Error(`API Hatası: ${response.status} ${response.statusText}`);
      }
      
      const data = response.data;
      console.log('API isteği başarılı');
      
      return data;
    } catch (error) {
      console.error('API isteği hatası:', error);
      throw error;
    }
  }

  // Token'ın geçerli olup olmadığını kontrol et
  async validateToken() {
    try {
      // Token varsa API'ye test isteği gönder
      if (this.getToken()) {
        // Config'de profile endpoint'i varsa kullan
        const profileEndpoint = apiConfig.ENDPOINTS.USER?.PROFILE || '/profile';
        await this.apiRequest(profileEndpoint);
        
        console.log('Token doğrulama başarılı');
        return true;
      }
      
      console.log('Token bulunamadı');
      return false;
    } catch (error) {
      console.warn('Token doğrulama başarısız:', error.message);
      // Token geçersizse temizle
      this.logout();
      return false;
    }
  }

  // 👤 User profil bilgilerini güncelle
  updateUser(userData) {
    const currentUser = this.getUser();
    if (currentUser) {
      const updatedUser = { ...currentUser, ...userData };
      localStorage.setItem(this.USER_KEY, JSON.stringify(updatedUser));
      
      console.log('Kullanıcı verisi güncellendi:', updatedUser.username);
      return updatedUser;
    }
    
    console.warn('Güncelleme için mevcut kullanıcı bulunamadı');
    return null;
  }

  // 🔐 Permission kontrol et
  hasPermission(permission) {
    const user = this.getUser();
    if (!user) {
      console.warn('⚠️ No user found for permission check');
      return false;
    }
    
    // Admin her şeyi yapabilir
    if (user.role === 'admin') {
    console.log(`Admin izni verildi: ${permission}`);
      return true;
    }
    
    // Diğer permission kontrolları burada yapılabilir
    // Örnek: user.permissions?.includes(permission)
    
            console.log(`✅ İzin verildi: ${permission}`);
    return true;
  }

  // ⏰ Token expiry kontrolü (JWT için)
  isTokenExpired() {
    const token = this.getToken();
    if (!token) return true;
    
    try {
      // JWT token mı kontrol et
      if (token.includes('.')) {
        // JWT token'ı decode et
        const payload = JSON.parse(atob(token.split('.')[1]));
        const currentTime = Date.now() / 1000;
        
        const isExpired = payload.exp < currentTime;
        
        if (isExpired) {
          console.warn('JWT token süresi doldu');
        }
        
        return isExpired;
      } else {
        // JWT değilse, manuel expiry kontrolü
        const user = this.getUser();
        if (!user || !user.loginTime) return true;
        
        const loginTime = new Date(user.loginTime);
        const now = new Date();
        const hoursPassed = (now - loginTime) / (1000 * 60 * 60);
        
        // 24 saat sonra token'ı expired say
        const isExpired = hoursPassed > 24;
        
        if (isExpired) {
          console.warn('Manuel token süresi dolması tespit edildi (24h+)');
        }
        
        return isExpired;
      }
    } catch (error) {
      console.warn('Token süresi kontrolü başarısız:', error);
      // Parse edilemezse güvenli olmak için expired say
      return true;
    }
  }

  // 🔄 Token yenileme
  async refreshToken() {
    try {
      const refreshToken = localStorage.getItem('tegim-refresh-token');
      if (!refreshToken) throw new Error('Yenileme tokenı bulunamadı');

      console.log('Token yenilenmeye çalışılıyor...');
      const response = await httpClient.post(apiConfig.auth.refresh, { refreshToken }, { 'Content-Type': 'application/json' });
      if (response.ok) {
        const data = response.data?.data || response.data;
        const newToken = data.token || data.access_token;
        if (newToken) {
          localStorage.setItem(this.TOKEN_KEY, newToken);
          console.log('Token başarıyla yenilendi');
          return { success: true };
        }
      }
      throw new Error('Token yenileme başarısız');
    } catch (error) {
      console.error('Token yenileme hatası:', error);
      this.logout(); // Token yenilenemezse logout yap
      return { success: false, error: error.message };
    }
  }

  // 🩺 API durumu kontrol et
  async checkAPIHealth() {
    try {
      console.log('API sağlığı kontrol ediliyor...');
      
      const healthEndpoint = `/health`;
      const response = await httpClient.get(healthEndpoint, { 'Content-Type': 'application/json' });
      const isHealthy = response.ok;
      
      if (isHealthy) {
        console.log('API sağlıklı');
        return { success: true, status: 'healthy' };
      } else {
        console.warn(`⚠️ API sağlık kontrolü başarısız: ${response.status}`);
        return { success: false, status: 'error', code: response.status };
      }
    } catch (error) {
      console.error('API sağlık kontrolü başarısız:', error);
      return { success: false, status: 'unreachable', error: error.message };
    }
  }

        // Yapılandırma güncellemelerini dinle
  onConfigUpdate() {
    // Config değiştiğinde API URL'i güncelle
    this.API_BASE_URL = apiConfig.BASE_URL;
    console.log('AuthService yapılandırması güncellendi:', this.API_BASE_URL);
  }

        // 📊 Hata ayıklama bilgileri
  getDebugInfo() {
    return {
      apiBaseURL: this.API_BASE_URL,
      configHost: apiConfig.API_SETTINGS.HOST,
      configPort: apiConfig.API_SETTINGS.PORT,
      hasToken: !!this.getToken(),
      hasUser: !!this.getUser(),
      isAuthenticated: this.isAuthenticated(),
      isTokenExpired: this.isTokenExpired(),
      developmentMode: apiConfig.DEVELOPMENT_MODE
    };
  }
}

// Singleton instance
const authService = new AuthService();

// Config değişikliklerini dinle
if (typeof window !== 'undefined') {
  // Config güncellendiğinde AuthService'i de güncelle
  window.addEventListener('configUpdated', () => {
    authService.onConfigUpdate();
  });
}

export default authService;
